import cx from "classnames";
import { ReactNode, useState } from "react";
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import Image from "common/components/Image";
import { ReviewRating } from "common/types";
import { Destination } from "common/router/types";
import LinkButton from "common/components/LinkButton";
import Icon from "common/components/Icon";
import { Optional } from "common/utils/types";

import ListOrder from "client/orders/components/ListOrder";
import ReviewCreateModal from "client/reviews/components/ReviewCreateModal";
import OrderModificationsModal from "client/orders/components/OrderModificationsModal";
import { urls } from "client/routes";
import truck from "client/common/images/truck.svg";
import search from "client/common/images/search.svg";
import script from "client/common/images/script.svg";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import ListSubscription from "client/subscriptions/components/ListSubscription";
import { useOrderCancelationDrawer } from "client/orders/stackables";

import { useSummaryQuery } from "../hooks";
import {
  DashboardDisplayKind,
  DashboardSummary,
  OrderDashboardSummary,
  SubscriptionDashboardSummary,
} from "../types";
import { getOrderDashboardSummaryBaseContentProps } from "../utils";

import styles from "../styles/LoggedInWelcomeSummary.module.less";

type BaseLoggedInSummaryContentProps = { title: string; description: string; children: ReactNode };

const BaseLoggedInSummaryContent = ({
  title,
  description,
  children,
}: BaseLoggedInSummaryContentProps) => {
  return (
    <div className={styles.loggedInSummaryContent}>
      <div className={styles.title}>{title}</div>
      <div className={styles.description}>{description}</div>
      <div className={styles.details}>{children}</div>
    </div>
  );
};

type SubscriptionLoggedInSummaryContentProps = {
  summary: SubscriptionDashboardSummary;
};

const SubscriptionLoggedInSummaryContent = ({
  summary,
}: SubscriptionLoggedInSummaryContentProps) => {
  const { subscriptions } = summary;
  const isPlural = subscriptions.length > 1;
  const orderPluralized = isPlural ? "orders" : "order";
  return (
    <>
      <BaseLoggedInSummaryContent
        title={isPlural ? "You have upcoming Autoships" : "You have an upcoming Autoship"}
        description={`Your ${orderPluralized} will be on the way soon! We've got everything covered, so you don't need to worry about a thing.`}
      >
        {subscriptions.map((subscription, i) => (
          <ListSubscription
            key={subscription.id}
            subscription={subscription}
            index={i}
            total={subscriptions.length}
          />
        ))}
      </BaseLoggedInSummaryContent>
    </>
  );
};

type OrderLoggedInSummaryContentProps = { summary: OrderDashboardSummary };

/**
 * Displays summary content for orders. Currently assumes all orders are shipped
 * TODO: add support for pickup orders in display strings
 * */
const OrderLoggedInSummaryContent = ({ summary }: OrderLoggedInSummaryContentProps) => {
  const { open: openOrderCancelationDrawer } = useOrderCancelationDrawer();
  const [reviewModalProps, setReviewModalProps] = useState<{
    productLineId: number;
    rating?: ReviewRating;
  } | null>(null);
  const [modifyingOrderId, setModifyingOrderId] = useState<number>(0);

  return (
    <>
      <BaseLoggedInSummaryContent {...getOrderDashboardSummaryBaseContentProps(summary)}>
        {summary.orders.map((order) => (
          <ListOrder
            key={order.id}
            order={order}
            onOpenReview={({ orderItem, rating }) =>
              setReviewModalProps({
                productLineId: orderItem.item.product.product_line.id,
                rating,
              })
            }
            onOpenModifications={() => setModifyingOrderId(order.id)}
            onOpenCancelation={() => openOrderCancelationDrawer({ orderId: order.id })}
          />
        ))}
      </BaseLoggedInSummaryContent>
      <ReviewCreateModal
        visible={!!reviewModalProps}
        onClose={() => setReviewModalProps(null)}
        initialRating={reviewModalProps?.rating}
        productLineId={reviewModalProps?.productLineId ?? 0}
      />
      <OrderModificationsModal
        visible={!!modifyingOrderId}
        onClose={() => setModifyingOrderId(0)}
        orderId={modifyingOrderId}
      />
    </>
  );
};

const exhaustiveKindCheck = (kind: never) => {
  console.error(`Unhandled kind: ${kind}`);
};

type LoggedInSummaryContentProps = {
  summary: Optional<DashboardSummary>;
  DefaultSummaryHeader?: React.ReactNode;
};

const LoggedInSummaryContent = ({
  summary,
  DefaultSummaryHeader = null,
}: LoggedInSummaryContentProps) => {
  if (!summary || !summary.kind) {
    // only display basic content when loading is complete to
    // avoid content flashing in and out.
    return <>{DefaultSummaryHeader}</>;
  }

  const { kind } = summary;

  // TODO sc-30389: include support for other kinds of summaries
  switch (kind) {
    case DashboardDisplayKind.Subscription:
      return <SubscriptionLoggedInSummaryContent summary={summary} />;
    case DashboardDisplayKind.Order:
      return <OrderLoggedInSummaryContent summary={summary} />;
    case DashboardDisplayKind.Cart:
      return <>{DefaultSummaryHeader}</>;
    default:
      exhaustiveKindCheck(kind);
      return <>{DefaultSummaryHeader}</>;
  }
};

type LoggedInSummaryActionProps = {
  dest: Destination;
  imgSrc: string;
  imgAlt: string;
  title: string;
  description: string;
};

const LoggedInSummaryAction = ({
  imgSrc,
  imgAlt,
  title,
  description,
  dest,
}: LoggedInSummaryActionProps) => {
  return (
    <LinkButton dest={dest} className={styles.summaryAction}>
      <Image src={imgSrc} alt={imgAlt} className={styles.image} />
      <div className={styles.info}>
        <div className={styles.title}>{title}</div>
        <div className={styles.description}>{description}</div>
      </div>
      <div className={styles.angleRight}>
        <Icon className={styles.angleRightIcon} icon={faArrowRight} />
      </div>
    </LinkButton>
  );
};

const LoggedInSummaryActions = () => {
  return (
    <div className={styles.loggedInSummaryActions}>
      <LoggedInSummaryAction
        dest={urls.orders.list()}
        imgSrc={truck}
        imgAlt="shipping truck"
        title="View and manage orders"
        description="Check the status of previous orders placed on our online store"
      />
      <LoggedInSummaryAction
        dest={urls.products.search()}
        imgSrc={search}
        imgAlt="search"
        title="Browse new medications"
        description="Search our catalog for a variety of pet medications, supplements, and Rx diets"
      />
      <LoggedInSummaryAction
        dest={urls.prescriptions.list()}
        imgSrc={script}
        imgAlt="script"
        title="Fill an existing prescription"
        description="Place an online order for items prescribed by your veterinarian"
      />
    </div>
  );
};

type LoggedInWelcomeSummaryProps = {
  DefaultSummaryHeader?: React.ReactNode;
};

const LoggedInWelcomeSummary = ({ DefaultSummaryHeader }: LoggedInWelcomeSummaryProps) => {
  const { data: summary, isLoading } = useSummaryQuery();

  if (isLoading) {
    return <LoadingContainer />;
  }

  return (
    <div className={cx(styles.loggedinWelcomeSummary)}>
      <LoggedInSummaryContent summary={summary} DefaultSummaryHeader={DefaultSummaryHeader} />
      <LoggedInSummaryActions />
    </div>
  );
};

export default LoggedInWelcomeSummary;
